// program_authoring.jsx — scheme and cycle authoring for the program editor.
//
//   <SchemeEditor>  the "Progression" picker for one program exercise, the
//                   scheme's settings (rep ranges, increments, training max,
//                   + set rule, back-offs, RIR, MEV → MRV) and a live preview
//                   of the next four weeks, all computed by progression.js.
//   <CycleCard>     weeks in the cycle, deload week, repeat or end.
// Everything writes through the store's program actions; progression.js owns
// the meaning of every field (withScheme / makeCycle / previewWeeks).

import React from 'react';
import { LLSheet } from './sheets.jsx';
import { unitLabel } from './store.jsx';
import { LL, LLIcon, LLCheck } from './tokens.jsx';
import { FilterChip } from './exercise_picker.jsx';
import {
  SCHEME_PICKER, WAVE_PICKER, TM_RULES, INC_CHOICES, WAVES, schemeOf, withScheme, previewWeeks, makeCycle,
  cycleFields, cycleOf, programWeekInfo, tmKeyOf, tmFor, incLabel, isSmall, dominantScheme, withCurrentWeek,
} from './progression.js';

// A program that gains a cycle starts it at week 1 now (past rotations of the
// old, cycle-less plan don't count as weeks of the new one).
export function cyclePatch(program, sessions, cycle) {
  if (!cycle) return { cycle: undefined };
  if (program.cycle) return { cycle };
  const fresh = { ...program, cycle };
  return { cycle, weekOffset: withCurrentWeek(fresh, sessions || [], 0).weekOffset };
}
import { kicker, card, meta, NumField, Field, Btn, toDisp, fromDisp, SchemeTag } from './program_ui.jsx';

// ——— Small pieces ——————————————————————————————————————————————————————————

// One-line segmented choice made of filter chips (wraps on narrow phones).
function Seg({ value, options, onChange, ariaLabel }) {
  return (
    <div role="radiogroup" aria-label={ariaLabel} style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map(o => (
        <FilterChip key={String(o.id)} active={value === o.id} onClick={() => onChange(o.id)}>{o.label}</FilterChip>
      ))}
    </div>
  );
}

const SCHEME_ICON = {
  double: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 15l4-4 3 3 7-8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  linear: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 16L17 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/><path d="M12 4h5v5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  percent: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M2.5 14c2-6 4-6 5 0s3 6 5 0 3-6 5 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>,
  rpe_topset: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><rect x="3" y="4" width="4" height="12" rx="1.2" stroke="currentColor" strokeWidth="1.6"/><rect x="9.5" y="9" width="3" height="7" rx="1" stroke="currentColor" strokeWidth="1.6"/><rect x="14.5" y="9" width="3" height="7" rx="1" stroke="currentColor" strokeWidth="1.6"/></svg>,
  gzcl: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 5h14M5.5 10h9M8 15h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
  rp_sets: (p) => <svg width="20" height="20" viewBox="0 0 20 20" fill="none" {...p}><path d="M3 16V12M7.5 16V9.5M12 16V7M16.5 16V4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
};

const round5 = (x) => Math.round(x * 2) / 2;

// Per-week RPE ramp for a top-set lift over the program's cycle.
function rpeRamp(program, start, peak) {
  const weeks = cycleOf(program).weeks;
  const work = weeks.filter(w => !w.deload).length;
  if (weeks.length <= 1) return start;
  let k = 0;
  return weeks.map(w => (w.deload ? 6 : round5(start + (peak - start) * (work > 1 ? k++ / (work - 1) : 0))));
}
function rpeBounds(cfg) {
  const r = cfg.rpe;
  if (Array.isArray(r)) { const work = r.filter(x => x > 6); return { start: work[0] ?? r[0] ?? 8, peak: Math.max(...(work.length ? work : r)) }; }
  return { start: typeof r === 'number' ? r : 8, peak: typeof r === 'number' ? r : 8 };
}

// A cycle that fits a scheme just switched on (when the program has none).
function cycleForScheme(program, pe) {
  const s = schemeOf(pe);
  const probe = { ...program, days: [{ exercises: [pe] }] };
  if (s === 'rp_sets') return makeCycle({ weeks: 5, deload: 'last', repeat: true }, probe);
  if (s === 'rpe_topset') return makeCycle({ weeks: 4, deload: 'last', repeat: true }, probe);
  if (s === 'percent') {
    const w = WAVES[pe.schemeCfg?.wave] || WAVES.w531;
    if (w.weeks.length <= 1) return null;
    if (pe.schemeCfg?.wave === 'w531') return makeCycle({ weeks: 4, deload: 'last', repeat: true }, probe);
    return { repeat: !w.testWeek, weeks: w.weeks.map((_, i) => ((w.deloadWeeks || []).includes(i) ? { label: 'Deload', deload: true } : { label: `Week ${i + 1}` })) };
  }
  return null;
}

// ——— Scheme picker sheet ———————————————————————————————————————————————————

function SchemePickerSheet({ open, onClose, value, onPick, name }) {
  return (
    <LLSheet open={open} onClose={onClose} title="Progression" subtitle={name}>
      <div role="radiogroup" aria-label="Progression scheme" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {SCHEME_PICKER.map(o => {
          const on = o.id === value;
          const Icon = SCHEME_ICON[o.id];
          return (
            <button key={o.id} type="button" role="radio" aria-checked={on} onClick={() => onPick(o.id)} style={{
              display: 'flex', alignItems: 'center', gap: 12, minHeight: 60, padding: '10px 12px', borderRadius: LL.r.md, cursor: 'pointer', textAlign: 'left',
              background: on ? `color-mix(in oklab, ${LL.accent} 12%, ${LL.surface2})` : LL.surface2,
              border: `1px solid ${on ? `color-mix(in oklab, ${LL.accent} 50%, transparent)` : LL.line}`,
            }}>
              <span style={{ width: 36, height: 36, borderRadius: LL.r.sm, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: on ? LL.accentSoft : LL.surface, color: on ? LL.accentText : LL.fgMute }}>
                <Icon />
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{o.label}</span>
                <span style={{ display: 'block', ...meta, fontSize: LL.fs.sm, marginTop: 2, lineHeight: 1.35 }}>{o.sub}</span>
              </span>
              <LLCheck on={on} />
            </button>
          );
        })}
      </div>
    </LLSheet>
  );
}

function WavePickerSheet({ open, onClose, value, onPick }) {
  return (
    <LLSheet open={open} onClose={onClose} title="Percentage wave" subtitle="Loads are percentages of the training max">
      <div role="radiogroup" aria-label="Wave" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {WAVE_PICKER.map(id => {
          const w = WAVES[id];
          const on = id === value;
          const pcts = w.weeks.flat().map(x => x.pct);
          return (
            <button key={id} type="button" role="radio" aria-checked={on} onClick={() => onPick(id)} style={{
              display: 'flex', alignItems: 'center', gap: 12, minHeight: 56, padding: '10px 12px', borderRadius: LL.r.md, cursor: 'pointer', textAlign: 'left',
              background: on ? `color-mix(in oklab, ${LL.accent} 12%, ${LL.surface2})` : LL.surface2,
              border: `1px solid ${on ? `color-mix(in oklab, ${LL.accent} 50%, transparent)` : LL.line}`,
            }}>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg }}>{w.label}</span>
                <span style={{ display: 'block', ...meta, fontSize: LL.fs.sm, marginTop: 2 }}>
                  {w.weeks.length} week{w.weeks.length === 1 ? '' : 's'} · {Math.min(...pcts)}–{Math.max(...pcts)}% · {TM_RULES.find(r => r.id === (w.tmRule || 'none'))?.label.toLowerCase()}
                </span>
              </span>
              <LLCheck on={on} />
            </button>
          );
        })}
      </div>
    </LLSheet>
  );
}

// ——— Four-week preview ————————————————————————————————————————————————————

function WeeksPreview({ program, pe, units, sessions }) {
  const info = React.useMemo(() => programWeekInfo({ program, sessions }), [program, sessions]);
  const weeks = previewWeeks({ program, pe, from: info.index, count: 4, units, history: sessions });
  const cyc = cycleOf(program);
  const hasWeeks = cyc.weeks.length > 1;
  return (
    <div style={{ marginTop: 10, padding: '10px 12px 4px', borderRadius: LL.r.md, background: LL.surface, border: `1px solid ${LL.line}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
        <span style={kicker}>{hasWeeks ? 'Next 4 weeks' : 'Next 4 sessions'}</span>
        {weeks.some(w => w.needsTM) && <span style={{ ...meta, fontSize: LL.fs.sm, display: 'inline-flex', alignItems: 'center', gap: 6 }}><span style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: LL.warn }} />Set a training max for loads</span>}
      </div>
      <div style={{ marginTop: 6 }}>
        {weeks.map((w, i) => (
          <div key={w.index} style={{ display: 'grid', gridTemplateColumns: '58px 1fr', gap: 10, padding: '8px 0', borderTop: i ? `1px solid ${LL.line}` : 'none', alignItems: 'baseline' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontFamily: LL.ui, fontSize: LL.fs.cap, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: i === 0 ? LL.accentText : LL.fgDim }}>{hasWeeks ? `Wk ${w.week + 1}` : i === 0 ? 'Next' : `+${i}`}</span>
                {i === 0 && <span aria-label="current" style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: LL.accent }} />}
              </div>
              {hasWeeks && <div style={{ ...meta, fontSize: LL.fs.cap, color: LL.fgDim, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.deload ? 'Deload' : String(w.label).replace(/^Week \d+ · /, '')}</div>}
            </div>
            <div style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, color: LL.fg, lineHeight: 1.4, fontVariantNumeric: 'tabular-nums', minWidth: 0, overflowWrap: 'anywhere' }}>{w.text}</div>
          </div>
        ))}
      </div>
      {!hasWeeks && ['double', 'linear', 'gzcl'].includes(schemeOf(pe)) && (
        <div style={{ ...meta, fontSize: LL.fs.sm, color: LL.fgDim, padding: '2px 0 8px', lineHeight: 1.4 }}>Loads move with your results after each session.</div>
      )}
    </div>
  );
}

// ——— SchemeEditor ——————————————————————————————————————————————————————————

export function SchemeEditor({ program, day, pe, units, actions, sessions, toast, bw = false, isSec = false }) {
  const [picker, setPicker] = React.useState(false);
  const [wavePicker, setWavePicker] = React.useState(false);
  const scheme = schemeOf(pe);
  const cfg = pe.schemeCfg || {};
  const ul = unitLabel(units);
  const up = (patch) => actions.updateExerciseInDay(program.id, day.id, pe.id, patch);
  const apply = (s, c) => {
    const next = withScheme(pe, s, c);
    up({ scheme: next.scheme, schemeCfg: next.schemeCfg, schemeState: next.schemeState, sets: next.sets, repMin: next.repMin, repMax: next.repMax });
    if (s !== scheme && !program.cycle) {
      const cyc = cycleForScheme(program, next);
      if (cyc) {
        actions.updateProgram(program.id, cyclePatch(program, sessions, cyc));
        toast && toast(`Added a ${cyc.weeks.length}-week cycle${cyc.weeks.some(w => w.deload) ? ' with a deload' : ''}`);
      }
    }
  };
  const setCfg = (patch) => apply(scheme, { ...cfg, ...patch });
  const pick = (s) => { setPicker(false); if (s !== scheme) apply(s, null); };
  const w = pe.targetWeight || 0;
  const smallStep = isSmall(pe);
  const incNow = cfg.inc ?? (scheme === 'percent' ? (smallStep ? 5 : 10) : smallStep ? 2.5 : 5);
  const incSeg = (label, value, onChange) => (
    <div style={{ gridColumn: '1 / -1' }}>
      <Field label={label}><Seg ariaLabel={label} value={value} onChange={onChange} options={INC_CHOICES.map(x => ({ id: x, label: `+${incLabel(x, units)}` }))} /></Field>
    </div>
  );
  const weightField = (label) => (
    <div style={{ gridColumn: '1 / -1' }}>
      <Field label={label} hint={w > 0 ? null : (bw ? 'Leave at 0 for bodyweight only' : 'Leave at 0 to find it in your first session')}>
        <NumField value={toDisp(w, units)} onChange={(v) => up({ targetWeight: fromDisp(v, units) })} min={0} max={units === 'kg' ? 500 : 1100} step={units === 'kg' ? 2.5 : 5} width="100%" ariaLabel={label} placeholder="0" />
      </Field>
    </div>
  );
  const repsMinMax = (
    <>
      <Field label={isSec ? 'Min seconds' : 'Min reps'}><NumField value={pe.repMin} onChange={(v) => up({ repMin: v, repMax: Math.max(v, pe.repMax) })} min={1} max={isSec ? 600 : 50} step={isSec ? 5 : 1} width="100%" ariaLabel="Minimum reps" decimals={0} /></Field>
      <Field label={isSec ? 'Max seconds' : 'Max reps'}><NumField value={pe.repMax} onChange={(v) => up({ repMax: v, repMin: Math.min(v, pe.repMin) })} min={1} max={isSec ? 600 : 50} step={isSec ? 5 : 1} width="100%" ariaLabel="Maximum reps" decimals={0} /></Field>
    </>
  );
  const restField = (
    <Field label="Rest"><NumField value={pe.rest ?? 120} onChange={(v) => up({ rest: v })} min={0} max={600} step={15} width="100%" ariaLabel="Rest seconds" format={(sec) => (sec >= 60 ? `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}` : `${sec}s`)} decimals={0} /></Field>
  );
  const restInline = scheme === 'double' || scheme === 'linear';
  const Icon = SCHEME_ICON[scheme];
  const current = SCHEME_PICKER.find(o => o.id === scheme);
  const tmKey = tmKeyOf(pe);
  const tm = tmFor(program, pe);

  let fields = null;
  if (scheme === 'double') {
    fields = (
      <>
        <Field label="Sets"><NumField value={pe.sets} onChange={(v) => up({ sets: v })} min={1} max={12} width="100%" ariaLabel="Sets" decimals={0} /></Field>
        {restField}
        {repsMinMax}
        {!isSec && incSeg('Add when every set tops the range', cfg.inc ?? (smallStep ? 2.5 : 5), (v) => up({ schemeCfg: { ...cfg, inc: v } }))}
        {!isSec && weightField(bw ? `Added weight (${ul})` : `Target weight (${ul})`)}
      </>
    );
  } else if (scheme === 'linear') {
    fields = (
      <>
        <Field label="Sets"><NumField value={pe.sets} onChange={(v) => setCfg({ sets: v })} min={1} max={10} width="100%" ariaLabel="Sets" decimals={0} /></Field>
        <Field label="Reps"><NumField value={pe.repMin} onChange={(v) => setCfg({ reps: v })} min={1} max={20} width="100%" ariaLabel="Reps" decimals={0} /></Field>
        {incSeg('Add every session', incNow, (v) => setCfg({ inc: v }))}
        <Field label="Misses before reset"><NumField value={cfg.failLimit || 3} onChange={(v) => setCfg({ failLimit: v })} min={1} max={5} width="100%" ariaLabel="Misses before a 10% reset" decimals={0} /></Field>
        {restField}
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Last set">
            <Seg ariaLabel="Last set" value={cfg.amrapLast ? 'plus' : 'fixed'} onChange={(v) => setCfg({ amrapLast: v === 'plus' || undefined })} options={[{ id: 'fixed', label: 'Same reps' }, { id: 'plus', label: 'As many as you can' }]} />
          </Field>
        </div>
        {weightField(`Next session (${ul})`)}
      </>
    );
  } else if (scheme === 'percent') {
    const wave = WAVES[cfg.wave] || WAVES.w531;
    const rule = cfg.tmRule || wave.tmRule || 'none';
    const cycLen = cycleOf(program).weeks.length;
    const mismatch = wave.weeks.length > 1 && cycLen % wave.weeks.length !== 0;
    fields = (
      <>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Wave">
            <button type="button" onClick={() => setWavePicker(true)} style={{
              width: '100%', minHeight: 44, display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderRadius: LL.r.sm, cursor: 'pointer', textAlign: 'left',
              background: LL.surface2, border: `1px solid ${LL.line}`, color: LL.fg, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600,
            }}>
              <span style={{ flex: 1, minWidth: 0 }}>{wave.label}<span style={{ ...meta, fontWeight: 500, marginLeft: 6 }}>{wave.weeks.length} wk</span></span>
              <LLIcon.chev style={{ color: LL.fgDim, flexShrink: 0 }} />
            </button>
          </Field>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label={`Training max (${ul})`} hint={tmKey !== pe.exerciseId ? `Shared with ${cfg.tmName || 'another lift'}` : (tm > 0 ? 'About 90% of your true one-rep max' : 'Enter ~90% of your max, or use a recent set from the plan card')}>
            <NumField value={toDisp(tm, units)} onChange={(v) => actions.updateProgram(program.id, { tms: { ...(program.tms || {}), [tmKey]: fromDisp(v, units) } })} min={0} max={units === 'kg' ? 500 : 1100} step={units === 'kg' ? 2.5 : 5} width="100%" ariaLabel="Training max" placeholder="0" />
          </Field>
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="The training max moves">
            <Seg ariaLabel="Training max rule" value={rule} onChange={(v) => setCfg({ tmRule: v === (wave.tmRule || 'none') ? undefined : v })} options={TM_RULES.map(r => ({ id: r.id, label: r.label }))} />
            <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 6, lineHeight: 1.4 }}>{TM_RULES.find(r => r.id === rule)?.sub}</div>
          </Field>
        </div>
        {rule !== 'none' && rule !== 'test' && rule !== 'rtf' && incSeg('Training max step', cfg.inc ?? (smallStep ? 5 : 10), (v) => setCfg({ inc: v }))}
        {mismatch && (
          <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: LL.r.md, background: LL.surface, border: `1px solid ${LL.line}` }}>
            <span style={{ ...meta, flex: 1, fontSize: LL.fs.sm, lineHeight: 1.4 }}>This wave is {wave.weeks.length} weeks; the program's cycle is {cycLen}.</span>
            <Btn size="sm" tone="soft" onClick={() => actions.updateProgram(program.id, { cycle: cycleForScheme({ ...program, cycle: undefined }, pe) })}>Match cycle</Btn>
          </div>
        )}
      </>
    );
  } else if (scheme === 'rpe_topset') {
    const { start, peak } = rpeBounds(cfg);
    fields = (
      <>
        <Field label="Top set reps"><NumField value={cfg.topReps || 3} onChange={(v) => setCfg({ topReps: v })} min={1} max={12} width="100%" ariaLabel="Top set reps" decimals={0} /></Field>
        <Field label="Back-off sets"><NumField value={cfg.backoffs ?? 3} onChange={(v) => setCfg({ backoffs: v })} min={0} max={8} width="100%" ariaLabel="Back-off sets" decimals={0} /></Field>
        <Field label="Back-off reps"><NumField value={cfg.backoffReps || cfg.topReps || 5} onChange={(v) => setCfg({ backoffReps: v })} min={1} max={15} width="100%" ariaLabel="Back-off reps" decimals={0} /></Field>
        <Field label="Back-offs lighter by"><NumField value={cfg.drop ?? 10} onChange={(v) => setCfg({ drop: v })} min={0} max={40} step={2.5} suffix="%" width="100%" ariaLabel="Back-off drop percent" /></Field>
        <Field label="RPE, first week"><NumField value={start} onChange={(v) => setCfg({ rpe: rpeRamp(program, Math.min(v, peak), Math.max(v, peak)) })} min={6} max={10} step={0.5} width="100%" ariaLabel="Starting RPE" /></Field>
        <Field label="RPE, peak week"><NumField value={peak} onChange={(v) => setCfg({ rpe: rpeRamp(program, Math.min(start, v), Math.max(start, v)) })} min={6} max={10} step={0.5} width="100%" ariaLabel="Peak RPE" /></Field>
        <div style={{ gridColumn: '1 / -1', ...meta, fontSize: LL.fs.sm, lineHeight: 1.4, padding: '0 2px' }}>
          Back-offs are recalculated from today's top set and its RPE. Deload weeks drop to RPE 6.
        </div>
        {weightField(`Next top set (${ul})`)}
      </>
    );
  } else if (scheme === 'gzcl') {
    const tier = [1, 2, 3].includes(cfg.tier) ? cfg.tier : 1;
    const desc = { 1: '5×3+ → 6×2+ → 10×1+; a miss moves a stage, failing 10×1 resets at 85% of a new 5RM.', 2: '3×10 → 3×8 → 3×6; a miss moves a stage, failing 3×6 restarts 3×10 a little heavier.', 3: '3×15+; 25 reps on the last set adds weight.' }[tier];
    fields = (
      <>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Tier">
            <Seg ariaLabel="Tier" value={tier} onChange={(v) => apply('gzcl', { ...cfg, tier: v })} options={[{ id: 1, label: 'T1 heavy' }, { id: 2, label: 'T2 volume' }, { id: 3, label: 'T3 pump' }]} />
            <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 6, lineHeight: 1.4 }}>{desc}</div>
          </Field>
        </div>
        {incSeg('Add when a stage is complete', incNow, (v) => setCfg({ inc: v }))}
        {weightField(`Working weight (${ul})`)}
      </>
    );
  } else if (scheme === 'rp_sets') {
    const mev = Math.max(1, cfg.mev || pe.sets || 2);
    const mrv = Math.max(mev, cfg.mrv || mev + 3);
    fields = (
      <>
        <Field label="Start (MEV) sets"><NumField value={mev} onChange={(v) => setCfg({ mev: v, mrv: Math.max(v, mrv) })} min={1} max={10} width="100%" ariaLabel="Minimum effective sets" decimals={0} /></Field>
        <Field label="Max (MRV) sets"><NumField value={mrv} onChange={(v) => setCfg({ mrv: v, mev: Math.min(v, mev) })} min={1} max={12} width="100%" ariaLabel="Maximum recoverable sets" decimals={0} /></Field>
        {repsMinMax}
        <Field label="RIR, first week"><Seg ariaLabel="Starting RIR" value={cfg.rirStart ?? 3} onChange={(v) => setCfg({ rirStart: v, rir: undefined })} options={[2, 3, 4].map(x => ({ id: x, label: String(x) }))} /></Field>
        <Field label="RIR, last week"><Seg ariaLabel="Final RIR" value={cfg.rirEnd ?? 0} onChange={(v) => setCfg({ rirEnd: v, rir: undefined })} options={[0, 1].map(x => ({ id: x, label: String(x) }))} /></Field>
        <div style={{ gridColumn: '1 / -1', ...meta, fontSize: LL.fs.sm, lineHeight: 1.4, padding: '0 2px' }}>
          Sets start at {mev} and climb toward {mrv}. After each workout your pump, soreness and joint ratings decide the next week: a good pump while recovered adds a set, still sore holds, joint pain holds and flags the lift.
        </div>
        {incSeg('Add when every set tops the range', incNow, (v) => setCfg({ inc: v }))}
        {weightField(`Working weight (${ul})`)}
      </>
    );
  }

  return (
    <div>
      <button type="button" onClick={() => setPicker(true)} aria-haspopup="dialog" style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: LL.r.md, cursor: 'pointer', textAlign: 'left',
        background: LL.surface, border: `1px solid ${LL.line}`, marginBottom: 10,
      }}>
        <span style={{ width: 36, height: 36, borderRadius: LL.r.sm, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: LL.accentSoft, color: LL.accentText }}><Icon /></span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', ...kicker }}>Progression</span>
          <span style={{ display: 'block', fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, color: LL.fg, marginTop: 1 }}>{current?.label}</span>
        </span>
        <span style={{ fontFamily: LL.ui, fontSize: LL.fs.meta, fontWeight: 600, color: LL.accentText, flexShrink: 0 }}>Change</span>
      </button>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {fields}
        {!restInline && <div style={{ gridColumn: '1 / -1' }}>{restField}</div>}
      </div>
      <WeeksPreview program={program} pe={pe} units={units} sessions={sessions} />
      {pe.schemeState?.joint === 'bad' && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: LL.r.md, background: `color-mix(in oklab, ${LL.warn} 12%, ${LL.surface})`, border: `1px solid color-mix(in oklab, ${LL.warn} 40%, transparent)`, ...meta, color: LL.fg }}>
          <span style={{ width: 6, height: 6, borderRadius: LL.r.xs, background: LL.warn, flexShrink: 0 }} />
          Joint pain was flagged last time. Sets are held; consider swapping this exercise.
        </div>
      )}
      <SchemePickerSheet open={picker} onClose={() => setPicker(false)} value={scheme} onPick={pick} name={pe.name} />
      <WavePickerSheet open={wavePicker} onClose={() => setWavePicker(false)} value={cfg.wave || 'w531'} onPick={(id) => { setWavePicker(false); setCfg({ wave: id }); }} />
    </div>
  );
}

// ——— Cycle builder ————————————————————————————————————————————————————————

export function CycleCard({ program, sessions, onChange }) {
  const f = cycleFields(program);
  const cyc = program.cycle ? cycleOf(program) : null;
  const set = (patch) => {
    const next = { ...f, ...patch };
    onChange(cyclePatch(program, sessions, next.weeks <= 1 ? null : makeCycle(next, program)));
  };
  const kind = dominantScheme(program);
  const summary = !cyc ? 'Every week is the same; loads progress session to session.'
    : `${cyc.weeks.length} weeks${cyc.weeks.some(w => w.deload) ? ` · deload in week ${cyc.weeks.map((w, i) => (w.deload ? i + 1 : null)).filter(Boolean).join(' and ')}` : ''} · ${cyc.repeat ? 'then repeats' : 'then the block ends'}`;
  return (
    <div style={{ ...card, marginTop: 16, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', minHeight: 64, boxSizing: 'border-box' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: LL.ui, fontSize: LL.fs.body, color: LL.fg }}>Cycle</span>
            {kind !== 'double' && <SchemeTag scheme={kind === 'w531' ? 'percent' : kind} />}
          </div>
          <div style={{ ...meta, fontSize: LL.fs.sm, marginTop: 2, lineHeight: 1.35 }}>{summary}</div>
        </div>
        <NumField value={f.weeks} onChange={(v) => set({ weeks: v, deload: v <= 1 ? 'none' : (f.weeks <= 1 ? 'last' : f.deload) })} min={1} max={16} suffix={f.weeks === 1 ? 'wk' : 'wks'} width={142} ariaLabel="Weeks in the cycle" decimals={0} />
      </div>
      {f.weeks > 1 && (
        <div style={{ borderTop: `1px solid ${LL.line}`, padding: '12px 14px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ ...meta, width: 64, flexShrink: 0 }}>Deload</span>
            <Seg ariaLabel="Deload" value={f.deload} onChange={(v) => set({ deload: v })} options={[{ id: 'last', label: `Week ${f.weeks}` }, { id: 'none', label: 'None' }]} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ ...meta, width: 64, flexShrink: 0 }}>Then</span>
            <Seg ariaLabel="After the last week" value={f.repeat ? 'repeat' : 'end'} onChange={(v) => set({ repeat: v === 'repeat' })} options={[{ id: 'repeat', label: 'Start again' }, { id: 'end', label: 'End the block' }]} />
          </div>
          <div className="llp-noscroll" style={{ display: 'flex', gap: 6, overflowX: 'auto', margin: '0 -14px', padding: '0 14px 2px', scrollbarWidth: 'none' }}>
            {cyc && cyc.weeks.map((w, i) => (
              <span key={i} style={{
                flexShrink: 0, padding: '6px 10px', borderRadius: LL.r.sm, fontFamily: LL.ui, fontSize: LL.fs.sm, fontWeight: 600, whiteSpace: 'nowrap',
                background: w.deload ? 'transparent' : LL.surface2, color: w.deload ? LL.fgMute : LL.fg,
                border: `1px ${w.deload ? 'dashed' : 'solid'} ${LL.line2}`,
              }}>{w.deload ? `Wk ${i + 1} · deload` : (w.label || `Week ${i + 1}`).replace(/^Week (\d+)/, 'Wk $1')}</span>
            ))}
          </div>
          {f.custom && <div style={{ ...meta, fontSize: LL.fs.sm, color: LL.fgDim, lineHeight: 1.4 }}>This cycle came with the program (deloads mid-block). Changing it here rebuilds the week names.</div>}
        </div>
      )}
    </div>
  );
}

export { Seg };
